import { useEffect } from 'react';

import { LogLevel, Logger } from '@suntex.core/logging';
import { Client, Common_VifModule, PlayerId } from '@suntex.fim/api-client';
import { $ArithmeticOperation, ArithmeticOperation } from '@suntex.fim/enums';

import { type VifGameName, VifGameView } from './VifGameView';
import { VifSequenceLine } from './VifSequenceLine';
import { VifVideo } from './VifVideo';
import { VifAddSubRulesView } from './dom/VifAddSubRulesView';
import { VifBlockView } from './dom/VifBlockView';
import { VifMulDivRulesView } from './dom/VifMulDivRulesView';
import { type VifOnComplete } from './games/types';
import {
  isVifBlockDomModule,
  isVifGameModule,
  isVifGearsModule,
  isVifRulesDomModule,
  vifBlockFlag,
  vifRulesAddSubFlag,
  vifRulesMulDivFlag,
} from './vifData';

// Standard module canvas size (matches Suntex-Frontend's ModuleConstants).
const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;
const GEARS_SIZE = 700;

export interface VifViewProps {
  api: Client;
  playerId: PlayerId;
  preview: boolean;
  vifModule: Common_VifModule | undefined;
  operation: ArithmeticOperation;
  stage: number;
  maxAllowedStage: number;
  /** URL of the player's VIF hub, navigated to after the final stage. */
  hubUrl: string;
  /** Host's sound directory, e.g. the CDN's `sounds/`. */
  soundDirectory?: string;
}

const logger = Logger.for('VifView', { level: LogLevel.Debug });

export function VifView({
  api,
  playerId,
  preview,
  vifModule,
  operation,
  stage,
  maxAllowedStage,
  hubUrl,
  soundDirectory = '/sounds/',
}: VifViewProps) {
  useEffect(() => {
    logger.debug(`VifView mounted`, {
      playerId,
      operation,
      stage,
      maxAllowedStage,
      hubUrl,
    });
  }, [playerId, operation, stage, maxAllowedStage, hubUrl]);

  // eslint-disable-next-line func-style
  const handleComplete: VifOnComplete = (pId, op, completedStage) => {
    logger.debug(`handleComplete`, {
      playerId: pId,
      operation: op,
      completedStage,
    });
    void advanceVifStage({
      api,
      playerId: pId as PlayerId,
      operation: op,
      completedStage,
      preview,
      hubUrl,
    });
  };

  if (!vifModule) return null;

  // Shared by every per-family view below - they differ only in `flag`.
  const commonProps = {
    api,
    playerId,
    moduleId: vifModule.moduleId,
    moduleVariant: vifModule.moduleVariant,
    preview,
    hubUrl,
    soundDirectory,
    onComplete: () => handleComplete(playerId, operation, stage),
  };

  return (
    <div>
      {isVifRulesDomModule(vifModule) ? (
        vifModule.filename === 'VIFRulesMulDiv' ? (
          <VifMulDivRulesView
            flag={vifRulesMulDivFlag(vifModule)}
            {...commonProps}
          />
        ) : (
          <VifAddSubRulesView
            flag={vifRulesAddSubFlag(vifModule)}
            {...commonProps}
          />
        )
      ) : isVifBlockDomModule(vifModule) ? (
        <VifBlockView flag={vifBlockFlag(vifModule)} {...commonProps} />
      ) : isVifGameModule(vifModule) ? (
        <VifGameView
          api={api}
          name={vifModule.filename as VifGameName}
          width={GAME_WIDTH}
          height={GAME_HEIGHT}
          params={{
            playerId,
            moduleId: vifModule.moduleId,
            moduleVariant: vifModule.moduleVariant,
            stage,
            preview,
            hubUrl,
            soundDirectory,
          }}
          onComplete={handleComplete}
        />
      ) : isVifGearsModule(vifModule) ? (
        <VifGameView
          api={api}
          name="VIFGears"
          width={GEARS_SIZE}
          height={GEARS_SIZE}
          params={{
            playerId,
            moduleId: vifModule.moduleId,
            moduleVariant: vifModule.moduleVariant,
            stage,
            preview,
            hubUrl,
            soundDirectory,
            pwaMode: false,
          }}
          onComplete={handleComplete}
        />
      ) : (
        <VifVideo
          operation={operation}
          stage={stage}
          onComplete={() => handleComplete(playerId, operation, stage)}
        />
      )}
      <VifSequenceLine
        operation={operation}
        maxStage={maxAllowedStage}
        preview={preview}
      />
    </div>
  );
}

async function advanceVifStage({
  api,
  playerId,
  operation: _operation,
  completedStage,
  preview,
  hubUrl,
}: {
  api: Client;
  playerId: PlayerId;
  operation: number;
  completedStage: number;
  preview: boolean;
  hubUrl: string;
}) {
  if (preview) return;

  // VIF can be sloppy about types; coerce defensively (as the original did).
  const stage = Number(completedStage);

  const operation = $ArithmeticOperation.from(_operation);

  const response = await api.put_Player_UpdateVifStage({
    playerId,
    operation: operation,
    stage,
  });

  const { slug } = $ArithmeticOperation.describe(operation);

  if (stage < response.stage) {
    // Advance to the next stage.
    location.href = `/modules/vif/${slug}/${stage + 1}`;
  } else {
    // Finished the last stage; return to the hub.
    location.href = hubUrl;
  }
}
